import React, {useEffect, useState} from "react";
import styled from 'styled-components';

import {useHistory, useParams} from "react-router-dom";
import {Input, Textarea} from "@chakra-ui/react";
import ResizeTextarea from "react-textarea-autosize";
import {ChevronDown} from "lucide-react";
import {Fullscreen, Main} from '../../components/fullscreen_view';
import Form from "../../components/form";
import useFeedback from "../../utils/use_feedback";
import {FixedTopBar} from "../../components/top_bar";
import ImageField from "../../components/image_field";
import {useCreatePost} from "../../api/submit";
import {useQuery} from "../../utils/router";
import {useSaveClicked} from "../../store";
import CommunityLogo from "../../components/community_logo";
import {useCommunities} from "../../api/communities";
import useModal from "../../modals";
import {themecolors} from "../../utils/themecolors";
import Sheet from "../../components/bottom-sheet/sheet";
import SelectCommunitySheet from "../../components/bottom-sheet/select_community";

const Community = styled.div``;
const Link = styled.div``;
const Title = styled.div``;
const Text = styled.div``;

const postFieldWhitelistByType = {
    media: ['community', 'title', 'link'],
    text: ['community', 'title', 'content'],
    link: ['community', 'title', 'link'],
};
const filterPostFields = (post) => {
    const result = {};
    result['type'] = (post.type === 'text') ? 'text' : 'link';
    for (const name of postFieldWhitelistByType[post.type]) {
        result[name] = post[name];
    }
    return result;
};

export const AutoResizeTextarea = React.forwardRef((props, ref) => {
    return (
        <Textarea
            minH="unset"
            overflow="hidden"
            w="100%"
            resize="none"
            ref={ref}
            maxRows={3}
            minRows={1}
            as={ResizeTextarea}
            {...props}
        />
    );
});

const Submit = styled(({className}) => {
    const modal = useModal();
    const history = useHistory();
    const {busy, error, feedbackCh} = useFeedback(true);

    const type = useQuery().type || "link";
    const {community} = useParams();
    const initialCommunity = /^(win|all|home|trending)$/.test((community||"").toLowerCase()) ? "" : community;

    const [post, setPost] = useState({
        type,
        community: initialCommunity,
        title: '',
        link: '',
    });
    const set = (name) => (value) => setPost((post) => ({...post, [name]: value}));
    const storeCommunity = useCommunities(feedbackCh, [post.community])[0];

    const createPost = useCreatePost(feedbackCh);
    const save = async () => {
        try {
            const {link} = await createPost(filterPostFields(post));
            history.replace(link);
        } catch {
            // error is displayed
        }
    };

    const saveClicked = useSaveClicked();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        (async () => {
            if (saveClicked) {
                if (!saving) {
                    setSaving(true);
                    await save();
                    setSaving(false);
                }
            }
        })().then(() => {}, () => {});
    }, [saveClicked]);

    const [selectOpen, setSelectOpen] = useState(false);
    const handleCommunity = () => {
        setSelectOpen(true);
    };

    const handleSelect = (community) => {
        setSelectOpen(false);
        set('community')(community);
        // handle
    }

    return (
        <Fullscreen className={className}>
            <div>

            <FixedTopBar back title="Create a Post" save={"Post"} black />
            <Main>

                <Form autocomplete="off">

                    <Community onClick={handleCommunity}>
                        <CommunityLogo name={post.community} />
                        {storeCommunity ? storeCommunity.name : 'Select a Community'}
                        <ChevronDown size={16} />
                    </Community>

                        <Title>
                            <AutoResizeTextarea type="title"
                                   focusBorderColor={"none"}
                                   placeholder={'Post title'}
                                   value={post.title}
                                   onChange={(e) => set('title')(e.currentTarget.value)}
                                   maxLength="300"
                                   autoComplete="off" />
                        </Title>

                    {post.type === 'media' && (
                        <ImageField
                            id='image'
                            value={post.link}
                            onChange={set('link')}
                            placeholder="Image"
                            required
                        />
                    )}
                    {post.type === 'text' && (
                        <Text>
                            <Textarea focusBorderColor={"none"} type="content" value={post.content} rows={10} placeholder={'Your text content'}
                                      onChange={(e) => set('content')(e.currentTarget.value)}
                            />
                        </Text>

                    )}
                    {post.type === 'link' && (
                            <Link>
                                <Input focusBorderColor={"none"} type="link"
                                       placeholder={"https://"}
                                       value={post.link}
                                       onChange={(e) => set('link')(e.currentTarget.value)} />
                            </Link>
                    )}


                    {error && <p>Post failed to save</p>}

                    {selectOpen && <Sheet onClose={() => setSelectOpen(false) } padding={"menu"} isMaxHeight><SelectCommunitySheet handleSelect={handleSelect} onClose={() => setSelectOpen(false) } /></Sheet>}

                </Form>
            </Main>
            </div>
        </Fullscreen>
    );
})`
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : theme.white_0};
  
  ${Main} {
    width: 100%;
    padding: 0 20px;
  }
  ${Community} {
    border-radius: 4px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
    padding: 12px 0 0 0;
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 16px;
  }
  ${Community} img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin-right: 12px;
  }
  ${Community} svg {
    margin-left: 12px;
    font-size: 18px;
  }
  textarea, input {
    border: none;
    outline: none;
    padding: 0;
    padding: 12px 0;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  }
  textarea::placeholder, input::placeholder {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_high_impact_text};
  }
  textarea {
    font-size: 16px;
  }
  ${Title} {
    margin-top: 8px;
  }
  ${Title} textarea {
    font-size: 19px;
    font-weight: 600;
  }
  ${Link}, ${Text} {
    margin: 0;
  }
  ${Link} input {
    font-size: 17px;
  }
`;

export default Submit;
